import React, { useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, DraggableLocation, Droppable, OnDragEndResponder } from "react-beautiful-dnd";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@mui/icons-material/Add";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { DealCreate } from "components/modals";
import { useCannotMoveToStageHook, useDidUpdate, useModals, useNavigate, usePrompt } from "hooks";
import { pickBy } from "lodash";
import { useDealActions, useDealQueriesSelect, usePickUserPermissions, useUserRoleSelector } from "store";
import {
  CreateDealStageData,
  DealStage,
  DealStageItem,
  DealStageItemNote,
  GetDealStagesData,
  UpdateDealStageData
} from "store/types";

import { CreatePipeline, NotePopup } from "..";
import { RoleName } from "../../../constants";
import { Card } from "../Pipeline-card/Card";
import { Column } from "../Pipeline-column/Column";
import { OpportunitiesColumn } from "../Pipeline-column/OpportunitiesColumn";
import { SharedDealsColumn } from "../Pipeline-column/SharedDealsColumn";

interface HandleDragColumnData {
  sourceIndex: number;
  targetIndex: number;
}

interface HandleDragCardData {
  destination: DraggableLocation;
  draggableId: string;
}

export interface NotePopup {
  anchorEl: HTMLButtonElement;
  dealStageItem: DealStageItem;
  stageId: number;
}
interface PipelineProps {}

export const Pipeline: React.FC<PipelineProps> = () => {
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [notePopup, setNotePopup] = useState<NotePopup | null>(null);
  const [data, setData] = useState<DealStage[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const dealQueries = useDealQueriesSelect();
  const role = useUserRoleSelector();
  const myDeals = usePickUserPermissions("deals");
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const isViewerPermissionDeals = myDeals && myDeals === "viewer";
  const { deal_title, order_by, filters } = dealQueries || {};

  const {
    getDealStagesAction,
    deleteDealStageAction,
    updateDealStageAction,
    createDealStageAction,
    reorderDealStagesAction,
    updateDealStageItemAction,
    createDealNoteAction,
    updateDealNoteAction
  } = useDealActions();
  const { deleteModal, createModal, errorModal } = useModals();
  const { showCannotMoveToStageModal } = useCannotMoveToStageHook();

  const createDeal = async (stageId: number) => {
    const data = await openPrompt({ Component: DealCreate, props: { stageId } });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  // Data sorted by order by default;
  // const { action, data, loading, setData } = useActionHook(getDealStagesAction<true>);

  const getDealStages = useCallback(
    async (data: GetDealStagesData<true> & { turnOnLoading?: boolean }) => {
      try {
        if (data.turnOnLoading) {
          setLoading(true);
        }
        const { data: stages, last_page } = await getDealStagesAction(data);
        if (data.page! >= last_page) {
          setHasMore(false);
        }

        setPage((prevState) => prevState + 1);
        setData((prevState) => {
          const updatedStages: DealStage[] = stages.map((newStage) => {
            const stage = prevState.find((item) => item.id === newStage.id);
            if (stage) {
              return {
                ...stage,
                deals: [...stage.deals, ...newStage.deals]
              };
            }
            return newStage;
          });

          return updatedStages;
        });
      } finally {
        if (data.turnOnLoading) {
          setLoading(false);
        }
      }
    },
    [setPage, hasMore]
  );

  const resetParams = useCallback(() => {
    setData([]);
    setPage(1);
    setLoading(false);
    setHasMore(true);
  }, []);

  // Initial API call
  useEffect(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    getDealStages({
      filters: filtersPayload,
      deal_title,
      with_deals: true,
      order_by,
      page: 1,
      turnOnLoading: true
    });
  }, []);

  // Listen queries, reset params and call api with default params
  useDidUpdate(() => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    resetParams();
    getDealStages({
      filters: filtersPayload,
      with_deals: true,
      deal_title,
      order_by,
      page: 1
    });
  }, [dealQueries]);

  const deleteColumn = useCallback(
    ({ title, id }: DealStage) => {
      deleteModal({
        element: "Delete",
        message: `Are you sure you want to delete the column: ${title}?`,
        action: async () => {
          await deleteDealStageAction(id);
          setData((prevState) => prevState.filter((item) => item.id !== id));
          // action({ assetClassId: assetClasses, statusId: statuses, title, with_deals: true, order_by });
        }
      });
    },
    [setData, deleteModal]
  );

  const updateColumn = useCallback(
    async (dataToSend: UpdateDealStageData) => {
      const stage = await updateDealStageAction(dataToSend);
      if (!data) return;
      setData(data.map((item) => (item.id === stage.id ? stage : item)));
      return stage;
    },
    [data]
  );

  const renameColumn = useCallback(
    (stage: DealStage) => {
      createModal({
        title: "Rename Column",
        action: async (title: string) => updateColumn({ id: stage.id, title }),
        submitButtonTitle: "Ok",
        defaultValue: stage.title
      });
    },
    [createModal, updateColumn]
  );

  const handleCreateColumn = useCallback(
    async (dataToSend: CreateDealStageData): Promise<void> => {
      const pipeline = await createDealStageAction(dataToSend);
      if (!data) return;
      setData([...data, pipeline]);
    },
    [createModal, data]
  );

  // Backend expects order without the hidden stage (DealStatusList.STATUS_HIDDEN_ID = 7); it validates size as DealStage::count() - 1
  const HIDDEN_STATUS_ID = 7;

  const handleDragColumn = useCallback(
    async ({ sourceIndex, targetIndex }: HandleDragColumnData) => {
      if (!data) return;
      const result = Array.from(data);
      // Remove element from the array
      const [removed] = result.splice(sourceIndex, 1);
      // Set the removed element in target slot
      result.splice(targetIndex, 0, removed);

      // Update components orders
      setData(result.map((item, index) => ({ ...item, order: index })));

      try {
        const orderableStages = result.filter((stage) => stage.status_id !== HIDDEN_STATUS_ID);
        await reorderDealStagesAction({
          order: orderableStages.map(({ id }, index) => ({ id, position: index }))
        });
      } catch (_unused) {
        errorModal({ msg: "Something went wrong" });
        setData(data); // Revert changes
      }
    },
    [data]
  );

  const handleDragCard = useCallback(
    async ({ destination, draggableId }: HandleDragCardData) => {
      if (!data || !destination) return;

      // Parse destination droppableId: format is "stage-{stageId}"
      const targetStageMatch = destination.droppableId.match(/stage-(\d+)/);
      if (!targetStageMatch) return;
      const targetStage = Number(targetStageMatch[1]);

      // Parse draggableId: "deal-{dealId}" (first two columns) or "stageId-dealId" (legacy)
      let sourceStage: number;
      let dealId: number;
      if (draggableId.startsWith("deal-")) {
        dealId = Number(draggableId.slice("deal-".length));
        const sourceStageData = data.find((stage) => stage.deals?.some((d) => d.id === dealId));
        if (!sourceStageData) return;
        sourceStage = sourceStageData.id;
      } else {
        const parts = draggableId.split("-");
        if (parts.length < 2) return;
        sourceStage = Number(parts[0]);
        dealId = Number(parts.slice(1).join("-"));
      }

      if (sourceStage === targetStage) return;

      // Find the deal to move
      const sourceStageData = data.find((stage) => stage.id === sourceStage);
      if (!sourceStageData) return;
      const stageItemToMove = sourceStageData.deals.find((deal) => deal.id === dealId);
      if (!stageItemToMove) return;

      // Optimistically update UI
      const updatedData = data.map((stage) => {
        if (stage.id === sourceStage) {
          return {
            ...stage,
            deals: stage.deals.filter((deal) => deal.id !== dealId),
            deals_count: Math.max(0, stage.deals_count - 1)
          };
        }

        if (stage.id === targetStage) {
          return {
            ...stage,
            deals: [stageItemToMove, ...stage.deals],
            deals_count: stage.deals_count + 1
          };
        }

        return stage;
      });

      setData(updatedData);

      // Revert optimistic update using the deal we captured (prevData no longer has it in source).
      const revertMove = () => {
        setData((prevData) =>
          prevData.map((stage) => {
            if (stage.id === sourceStage) {
              return { ...stage, deals: [...stage.deals, stageItemToMove], deals_count: stage.deals_count + 1 };
            }
            if (stage.id === targetStage) {
              return {
                ...stage,
                deals: stage.deals.filter((deal) => deal.id !== dealId),
                deals_count: Math.max(0, stage.deals_count - 1)
              };
            }
            return stage;
          })
        );
      };

      try {
        await updateDealStageItemAction({ dealId, deal_stage_id: targetStage });
      } catch (error: unknown) {
        // ApiProvider rejects with ApiServiceError: .status and .errors (errors = response.data.errors).
        const err = error as {
          status?: number;
          errors?: {
            code?: string;
            missing_fields?: Array<{ id: number; name: string }>;
            target_status?: string;
            target_stage_id?: number;
          };
          response?: {
            status: number;
            data?: {
              errors?: {
                code?: string;
                missing_fields?: Array<{ id: number; name: string }>;
                target_status?: string;
                target_stage_id?: number;
              };
            };
          };
        };
        const status = err.status ?? err.response?.status;
        const errors = err.errors ?? err.response?.data?.errors;
        if (
          status === 422 &&
          errors?.code === "status_transition_validation_failed" &&
          errors?.missing_fields?.length
        ) {
          const targetStageName = data.find((s) => s.id === targetStage)?.title ?? "this stage";
          revertMove();
          showCannotMoveToStageModal({
            dealId,
            targetStageName,
            targetStageId: targetStage,
            missingFields: errors.missing_fields
          });
        } else {
          errorModal({ msg: "Something went wrong" });
          revertMove();
        }
      }
    },
    [updateDealStageItemAction, errorModal, showCannotMoveToStageModal, data]
  );

  const handleDrag: OnDragEndResponder = useCallback(
    async ({ destination, source, type, draggableId, reason }) => {
      try {
        // If drag was cancelled, don't do anything
        if (reason === "CANCEL" || !destination) {
          return;
        }

        if (type === "PIPELINE") {
          if (typeof destination.index !== "number") return;
          await handleDragColumn({ sourceIndex: source.index, targetIndex: destination.index });
        }

        if (type === "STAGE") {
          await handleDragCard({ destination, draggableId });
        }
      } catch (error) {
        console.error("Drag error:", error);
        // Don't throw - let react-beautiful-dnd handle the error state
      }
    },
    [handleDragColumn, handleDragCard]
  );

  const saveDealNote = useCallback(
    async (value: string) => {
      let respondedNote: DealStageItemNote | null = null;

      if (!notePopup || !data) return;

      const {
        stageId,
        dealStageItem: { deal_note, id: dealId }
      } = notePopup;
      const { id } = deal_note || {};

      if (!dealId) return;

      try {
        if (id) {
          respondedNote = await updateDealNoteAction({ id, note: value, dealId });
        } else {
          respondedNote = await createDealNoteAction({ dealId, note: value });
        }

        // Find stage => deal and update note
        setData(
          data.map((stage) => {
            if (stage.id !== stageId) return stage;
            return {
              ...stage,
              deals: stage.deals.map((deal) => {
                if (deal.id !== dealId) return deal;
                return { ...deal, deal_note: respondedNote };
              })
            };
          })
        );
      } catch (_unused) {
        errorModal({ msg: "Something went wrong" });
      }
    },
    [data, notePopup]
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        overflow: "hidden"
      }}
    >
      {loading && !data.length ? (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <CircularProgress size={150} />
        </Stack>
      ) : (
        Boolean(data.length) && (
          <InfiniteScroll
            height="100%"
            next={() => {
              const filtersPayload = pickBy(filters, (value) => value.length > 0);
              getDealStages({
                filters: filtersPayload,
                deal_title,
                with_deals: true,
                order_by,
                page
              });
            }}
            hasMore={hasMore}
            dataLength={page * 50}
            loader={
              <Stack height="100%" justifyContent="center" alignItems="center">
                <CircularProgress size={150} />
              </Stack>
            }
          >
            <DragDropContext onDragEnd={handleDrag}>
              <Droppable droppableId="pipeline" direction="horizontal" type="PIPELINE">
                {(provided) => (
                  <Stack
                    direction="row"
                    gap="12px"
                    paddingBottom="10px"
                    alignItems="stretch"
                    minHeight="100%"
                    flexGrow="1"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {data[0] && Boolean(data[0].deals?.length) && (
                      <OpportunitiesColumn stage={data[0]} updateColumn={updateColumn} />
                    )}
                    {data[1] && Boolean(data[1].deals?.length) && (
                      <SharedDealsColumn stage={data[1]} updateColumn={updateColumn} />
                    )}

                    {data &&
                      data.map((item, index) => {
                        if (index < 2) return null;
                        if (item.status_id === 100) return null;
                        return (
                          <Draggable
                            isDragDisabled={role?.name === RoleName.TeamViewer || isViewerPermissionDeals}
                            key={item.id}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(colProvided, snapshot) => (
                              <Column
                                onDeleteColumn={deleteColumn}
                                onRenameColumn={renameColumn}
                                onCreateDeal={(stageId) => createDeal(stageId)}
                                updateColumn={updateColumn}
                                stage={item}
                                key={item.id}
                                draggableProvided={colProvided}
                                draggableSnapshot={snapshot}
                                setNotePopup={setNotePopup}
                                renderCard={(dealItem, draggableProvided, isDragging, stageId, setNotePopupFn) => (
                                  <Card
                                    saveNote={(anchorEl) => {
                                      if (setNotePopupFn) {
                                        setNotePopupFn({ anchorEl, dealStageItem: dealItem, stageId });
                                      }
                                    }}
                                    {...dealItem}
                                    draggableProvided={draggableProvided}
                                    isDragging={isDragging}
                                    stageId={stageId}
                                  />
                                )}
                              />
                            )}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                    {!loading && Boolean(data.length) && (
                      <RoleComponentGuard
                        availableRoles={[RoleName.Admin, RoleName.Analyst]}
                        hideChild={isViewerPermissionDeals}
                      >
                        <Button
                          sx={{
                            minWidth: "219px",
                            alignSelf: "flex-start",
                            whiteSpace: "nowrap",
                            p: "8px 0 8px 12px",
                            bgcolor: "white",
                            borderRadius: "8px",
                            justifyContent: "start",
                            color: "#0a3142",
                            fontWeight: "bold"
                          }}
                          startIcon={<AddIcon sx={{ stroke: "#0a3142" }} />}
                          onClick={() => setIsCreate(true)}
                        >
                          Add
                        </Button>
                      </RoleComponentGuard>
                    )}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </InfiniteScroll>
        )
      )}

      <CreatePipeline isOpen={isCreate} onClose={() => setIsCreate(false)} onSubmit={handleCreateColumn} />
      <NotePopup {...(notePopup && notePopup)} handleClose={() => setNotePopup(null)} onSubmit={saveDealNote} />
    </Box>
  );
};
